import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {buildDataset, mean} from "../core/stats";

// ===== FUNCIONES =====
const median = (arr) => {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a,b)=>a-b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const mode = (arr) => {
  if (!arr.length) return "-";
  const freq = {};
  let maxFreq = 0;
  let modes = [];
  arr.forEach(v => {
    freq[v] = (freq[v] || 0) + 1;
    if (freq[v] > maxFreq) maxFreq = freq[v];
  });
  if (maxFreq === 1) return "__nomode__";
  for (let k in freq) {
    if (freq[k] === maxFreq) modes.push(Number(k));
  }
  return modes.join(", ");
};

const variance = (arr) => {
  if (!arr.length) return 0;
  const m = mean(arr);
  return arr.reduce((acc, v) => acc + Math.pow(v - m, 2), 0) / arr.length;
};

const stdDev = (arr) => Math.sqrt(variance(arr));

const coefVar = (arr) => {
  const m = mean(arr);
  if (m === 0) return 0;
  return stdDev(arr) / m;
};

// ===== SUB-COMPONENTES =====
function StatCard({ label, value }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="stat-row">
      <span className="stat-row-label">{label}</span>
      <span className="stat-row-value">{value}</span>
    </div>
  );
}

// ===== COMPONENTE PRINCIPAL =====
export default function StatsPanel({hangers, cfg}) {
  const { t } = useTranslation();

  const data = useMemo(() => buildDataset(hangers), [hangers]);

  const dataMean     = useMemo(() => mean(data),     [data]);
  const dataMedian   = useMemo(() => median(data),   [data]);
  const dataMode     = useMemo(() => mode(data),     [data]);
  const dataVariance = useMemo(() => variance(data), [data]);
  const dataStdDev   = useMemo(() => stdDev(data),   [data]);
  const dataCoefVar  = useMemo(() => coefVar(data),  [data]);

  const dispersion = useMemo(() => {
    if (!data.length) return {min: 0, max: 0, range: 0};
    let min = data[0], max = data[0];
    for (const v of data) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { min, max, range: max - min };
  }, [data]);

  if (data.length === 0) return (
    <div className="stats-empty">{t("stats.empty")}</div>
  );

  const noMode = t("stats.noMode");

  return (
    <div className="statsBox">

      {/* Datos brutos */}
      <div className="stat-dataset">
        <span className="stat-dataset-label">{t("stats.data")}</span>
        <span className="stat-dataset-values">{"{ " + data.join(", ") + " }"}</span>
      </div>

      {/* Resumen: N, Mín, Máx */}
      <div className="stat-cards-row">
        <StatCard label={t("stats.n")}   value={data.length} />
        <StatCard label={t("stats.min")} value={dispersion.min} />
        <StatCard label={t("stats.max")} value={dispersion.max} />
      </div>

      {/* Estadísticos detallados */}
      {cfg.showStats && (
        <div className="stat-rows">
          {cfg.showMean     && <StatRow label={t("stats.meanFull")}    value={dataMean.toFixed(2)} />}
          {cfg.showMedian   && <StatRow label={t("stats.medianFull")}  value={dataMedian} />}
          {cfg.showMode     && <StatRow label={t("stats.modeFull")}    value={dataMode === "__nomode__" ? noMode : dataMode} />}
          {cfg.showRange    && <StatRow label={t("stats.rangeFull")}   value={dispersion.range} />}
          {cfg.showVariance && <StatRow label={t("stats.varianceFull")} value={dataVariance.toFixed(2)} />}
          {cfg.showStdDev   && <StatRow label={t("stats.stdDevFull")}  value={dataStdDev.toFixed(2)} />}
          {cfg.showCoefVar  && <StatRow label={t("stats.coefVarFull")} value={dataCoefVar.toFixed(2)} />}
        </div>
      )}

    </div>
  );
}
